import { endpoint, env, toResponseError, getAuthToken, handleUnauthorized, } from '@eruvaka/web-pondlogs-base';
import { readSessionFarms } from '../utils/farmSession';
const ACTIVE_LOCATION_KEY = 'ponds_active_location_id';
const buildUrl = (path, query) => {
    const resolvedPath = endpoint(env.apiBaseUrl, path);
    if (!query)
        return resolvedPath;
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
        if (value === undefined)
            return;
        params.set(key, String(value));
    });
    const queryString = params.toString();
    if (!queryString)
        return resolvedPath;
    return `${resolvedPath}${resolvedPath.includes('?') ? '&' : '?'}${queryString}`;
};
const isRecord = (value) => {
    return typeof value === 'object' && value !== null;
};
const toPondStatus = (value) => {
    if (typeof value !== 'string')
        return 'inactive';
    const normalized = value.trim().toLowerCase();
    if (normalized === 'active')
        return 'active';
    if (normalized === 'deleted')
        return 'deleted';
    return 'inactive';
};
const toPondStatusFull = (value) => {
    if (typeof value !== 'string')
        return 'inactive';
    const normalized = value.trim().toLowerCase();
    if (normalized === 'active')
        return 'active';
    if (normalized === 'deleted')
        return 'deleted';
    return 'inactive';
};
const toSpeciesType = (value) => {
    if (typeof value !== 'string')
        return 'Other';
    const normalized = value.trim().toLowerCase();
    if (normalized === 'shrimp' || normalized === 'vannamei')
        return 'Vannamei';
    return 'Other';
};
const toNumber = (value) => {
    if (typeof value === 'number' && Number.isFinite(value))
        return value;
    if (typeof value === 'string') {
        const parsed = Number(value);
        if (Number.isFinite(parsed))
            return parsed;
    }
    return 0;
};
const toNullableNumber = (value) => {
    if (typeof value === 'number' && Number.isFinite(value))
        return value;
    if (typeof value === 'string') {
        const parsed = Number(value);
        if (Number.isFinite(parsed))
            return parsed;
    }
    return null;
};
const toNullableString = (value) => {
    if (typeof value !== 'string')
        return null;
    const normalized = value.trim();
    return normalized || null;
};
const toPondListItem = (value) => {
    if (!isRecord(value))
        return null;
    const id = typeof value.id === 'string' ? value.id : typeof value._id === 'string' ? value._id : '';
    const name = typeof value.name === 'string' ? value.name : typeof value.title === 'string' ? value.title : '';
    if (!id || !name)
        return null;
    // BFF populates location_id as { _id, name, city, state } — extract the _id string
    const locationIdRaw = value.location_id;
    const farmId = isRecord(locationIdRaw)
        ? typeof locationIdRaw._id === 'string'
            ? locationIdRaw._id
            : String(locationIdRaw._id ?? '')
        : typeof locationIdRaw === 'string'
            ? locationIdRaw
            : undefined;
    return {
        id,
        name,
        status: toPondStatus(value.status),
        areaHectares: toNumber(value.areaHectares ?? value.area_hectares ?? value.size),
        speciesType: toSpeciesType(value.speciesType ?? value.species_type ?? value.species),
        plStocked: toNumber(value.post_larva_stocked),
        densityPerSqm: toNumber(value.density),
        doc: toNumber(value.doc ?? value.cultivation_cycle),
        shrimpsPerPm: toNumber(value.shrimps_per_pm ?? value.shrimps_pm),
        ...(farmId ? { farmId } : {}),
        isTest: Boolean(value.is_test),
        pauseAlerts: Boolean(value.pause_alerts),
        cultivationDate: toNullableString(typeof value.cultivation_date === 'string' ? value.cultivation_date : null),
        aeratorHp: toNullableNumber(value.aerator_hp) ?? undefined,
        hatcheryName: toNullableString(typeof value.hatchery_name === 'string' ? value.hatchery_name : null) ??
            undefined,
    };
};
const toPond = (value) => {
    const item = toPondListItem(value);
    const record = isRecord(value) ? value : {};
    return {
        id: item?.id ?? '',
        name: item?.name ?? '',
        areaHectares: item?.areaHectares ?? 0,
        depthMeters: record.depth_meters === null || record.depth === null
            ? null
            : toNullableNumber(record.depth_meters ?? record.depth),
        speciesType: item?.speciesType ?? 'Other',
        status: toPondStatusFull(record.status),
        farmId: typeof record.farmId === 'string'
            ? record.farmId
            : typeof record.location_id === 'string'
                ? record.location_id
                : '',
        createdAt: typeof record.createdAt === 'string'
            ? record.createdAt
            : typeof record.created_at === 'string'
                ? record.created_at
                : '',
        updatedAt: typeof record.updatedAt === 'string'
            ? record.updatedAt
            : typeof record.updated_at === 'string'
                ? record.updated_at
                : '',
        doc: toNullableNumber(record.doc ?? record.cultivation_cycle) ?? undefined,
        cultivationDate: toNullableString(record.cultivation_date),
        alertsCount: toNullableNumber(record.alerts_count ?? record.open_alerts_count) ??
            (Array.isArray(record.alerts) ? record.alerts.length : undefined),
    };
};
const extractPondRecord = (payload) => {
    if (!isRecord(payload))
        return {};
    const level1Data = isRecord(payload.data) ? payload.data : undefined;
    const level2Data = level1Data && isRecord(level1Data.data) ? level1Data.data : undefined;
    const candidates = [
        payload,
        payload.pond,
        level1Data,
        level1Data?.pond,
        level2Data,
        level2Data?.pond,
    ];
    for (const candidate of candidates) {
        if (!isRecord(candidate))
            continue;
        if (toPondListItem(candidate) !== null)
            return candidate;
    }
    for (const candidate of candidates) {
        if (isRecord(candidate))
            return candidate;
    }
    return {};
};
const extractLatestAbwRecord = (payload) => {
    if (!isRecord(payload))
        return {};
    if (isRecord(payload.pond) && Array.isArray(payload.pond.latest_records)) {
        const first = payload.pond.latest_records[0];
        return isRecord(first) ? first : {};
    }
    if (isRecord(payload.data) &&
        isRecord(payload.data.pond) &&
        Array.isArray(payload.data.pond.latest_records)) {
        const first = payload.data.pond.latest_records[0];
        return isRecord(first) ? first : {};
    }
    return {};
};
const toIsoUtcStartOfDay = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime()))
        return null;
    date.setUTCHours(0, 0, 0, 0);
    return date.toISOString();
};
const toIsoUtcEndOfDay = (value) => {
    const date = new Date(value);
    date.setUTCHours(23, 59, 59, 999);
    return date.toISOString();
};
const extractArrayFromPayload = (payload) => {
    if (Array.isArray(payload))
        return payload;
    if (!isRecord(payload))
        return [];
    if (Array.isArray(payload.data))
        return payload.data;
    if (isRecord(payload.data) && Array.isArray(payload.data.data))
        return payload.data.data;
    return [];
};
const extractObjectData = (payload) => {
    if (!isRecord(payload))
        return {};
    if (isRecord(payload.data))
        return payload.data;
    return payload;
};
const toApiStatus = (status) => {
    if (!status)
        return undefined;
    if (status === 'active')
        return 'ACTIVE';
    if (status === 'inactive')
        return 'INACTIVE';
    return 'DELETED';
};
const toApiMutationPayload = (payload) => {
    const body = {};
    if (payload.name !== undefined)
        body.title = payload.name;
    if (payload.areaHectares !== undefined)
        body.size = payload.areaHectares;
    if (payload.boundaryCoordinates !== undefined) {
        body.geometry = {
            type: 'Polygon',
            coordinates: [payload.boundaryCoordinates],
        };
    }
    if (payload.plStocked !== undefined)
        body.post_larva_stocked = payload.plStocked;
    // depth_meters is not in BFF updatePondSchema — omitted intentionally
    // status is not in BFF updatePondSchema — omitted intentionally (lifecycle managed via activate/restore/delete)
    if (payload.speciesType !== undefined && payload.speciesType !== 'Other')
        body.species = payload.speciesType;
    if (payload.farmId !== undefined)
        body.location_id = payload.farmId;
    if (payload.cultivationDate !== undefined)
        body.cultivation_date = payload.cultivationDate;
    if (payload.aeratorHp !== undefined)
        body.aerator_hp = payload.aeratorHp;
    if (payload.hatcheryName !== undefined)
        body.hatchery_name = payload.hatcheryName;
    return body;
};
const toApiCreatePayload = (payload, resolvedLocationId) => {
    const units = payload.units === 'hectares' ? 'ha' : payload.units === 'acres' ? 'ac' : payload.units;
    return {
        title: payload.title,
        size: payload.size,
        location_id: resolvedLocationId,
        units,
        geometry: {
            type: 'Polygon',
            coordinates: [payload.pond_coordinates],
        },
        // timezone is stored in the Pond model but not validated by the BFF Zod schema
        // Sent as extra context so the service can persist it if desired
        timezone: payload.timezone
            ? { name: payload.timezone.name.trim(), offset: payload.timezone.offset }
            : undefined,
    };
};
function resolveCreateLocationId(payload) {
    const payloadLocationId = payload.location_id.trim();
    if (payloadLocationId)
        return payloadLocationId;
    try {
        const activeLocationId = localStorage.getItem(ACTIVE_LOCATION_KEY)?.trim();
        if (activeLocationId)
            return activeLocationId;
    }
    catch {
        // Ignore storage errors and continue with session fallback.
    }
    if (typeof window !== 'undefined') {
        try {
            const locationIdFromUrl = new URLSearchParams(window.location.search).get('location_id');
            if (typeof locationIdFromUrl === 'string' && locationIdFromUrl.trim()) {
                return locationIdFromUrl.trim();
            }
        }
        catch {
            // Ignore URL parsing errors and continue with session fallback.
        }
    }
    const { selectedLocationId } = readSessionFarms();
    if (selectedLocationId)
        return selectedLocationId;
    // Return empty string as last resort - let the API validate
    return '';
}
const extractPondArray = (payload) => {
    if (Array.isArray(payload))
        return payload;
    if (!isRecord(payload))
        return [];
    if (Array.isArray(payload.ponds))
        return payload.ponds;
    if (Array.isArray(payload.data))
        return payload.data;
    if (isRecord(payload.data)) {
        if (Array.isArray(payload.data.ponds))
            return payload.data.ponds;
        if (Array.isArray(payload.data.items))
            return payload.data.items;
    }
    return [];
};
const normalizePondList = (payload) => {
    return extractPondArray(payload)
        .map(toPondListItem)
        .filter((item) => item !== null);
};
const parseJson = async (response) => {
    try {
        return await response.json();
    }
    catch {
        return {};
    }
};
const request = async (method, path, body) => {
    const token = getAuthToken();
    const requestInitBase = {
        method,
        credentials: 'include',
        body: body !== undefined ? JSON.stringify(body) : undefined,
    };
    const response = await fetch(endpoint(env.apiBaseUrl, path), {
        ...requestInitBase,
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
    });
    if (!response.ok) {
        const body = await response.text();
        if (response.status === 401) {
            handleUnauthorized();
            throw toResponseError(response.status, body);
        }
        throw toResponseError(response.status, body);
    }
    return parseJson(response);
};
const requestUrl = async (method, url, extraHeaders) => {
    const token = getAuthToken();
    const requestInitBase = {
        method,
        credentials: 'include',
    };
    const response = await fetch(url, {
        ...requestInitBase,
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...extraHeaders,
        },
    });
    if (!response.ok) {
        const body = await response.text();
        if (response.status === 401) {
            handleUnauthorized();
            throw toResponseError(response.status, body);
        }
        throw toResponseError(response.status, body);
    }
    return parseJson(response);
};
const readActiveLocationId = () => {
    try {
        return localStorage.getItem(ACTIVE_LOCATION_KEY)?.trim() || undefined;
    }
    catch {
        return undefined;
    }
};
export const fetchPonds = async (options = {}) => {
    const page = options.page ?? 1;
    const limit = Math.min(options.limit ?? 10, 100);
    const locationId = options.locationId ?? readActiveLocationId();
    const url = buildUrl('/api/v3/ponds', {
        page,
        limit,
        location_id: locationId,
        status: toApiStatus(options.status),
    });
    const locationHeaders = locationId ? { 'x-location-id': locationId } : {};
    const payload = await requestUrl('GET', url, locationHeaders);
    const record = isRecord(payload) ? payload : {};
    // BFF wraps pagination in a { pagination: { page, limit, total, totalPages, hasNext } } object.
    // Fall back to flat top-level keys for legacy compatibility.
    const pg = isRecord(record.pagination) ? record.pagination : record;
    return {
        ponds: normalizePondList(payload),
        total: toNumber(pg.total ?? record.total),
        page: toNumber(pg.page ?? record.page) || page,
        limit: toNumber(pg.limit ?? record.limit) || limit,
        totalPages: toNumber(pg.totalPages ?? pg.total_pages ?? record.total_pages) || 1,
        hasMore: Boolean(pg.hasNext ?? pg.has_more ?? record.has_more),
        message: typeof record.message === 'string' ? record.message : undefined,
    };
};
export const fetchPondById = async (id) => toPond(extractPondRecord(await request('GET', `/api/v3/ponds/${id}`)));
export const fetchPondKpiSummary = async (pond) => {
    const fallback = {
        abw: null,
        survivalPercentage: null,
        biomass: null,
        feedDispensed: null,
        doc: pond.doc ?? null,
        alertsCount: pond.alertsCount ?? null,
    };
    const cultureStartBase = pond.cultivationDate ?? pond.createdAt;
    const fromDate = cultureStartBase ? toIsoUtcStartOfDay(cultureStartBase) : null;
    const toDate = toIsoUtcEndOfDay(new Date());
    try {
        const [abwPayload, feedPayload, growthPayload] = await Promise.all([
            requestUrl('GET', buildUrl(`/api/v3/ponds/${pond.id}/latest-ABW-values-v2`, { no_of_records: 1 })),
            fromDate
                ? requestUrl('GET', buildUrl(`/api/v3/ponds/${pond.id}/feed-consumption-summary`, {
                    from_date: fromDate,
                    to_date: toDate,
                }))
                : Promise.resolve({}),
            fromDate
                ? requestUrl('GET', buildUrl(`/api/v3/ponds/${pond.id}/growth-vs-biomass`, {
                    cultivation_start_date: fromDate,
                }))
                : Promise.resolve([]),
        ]);
        const latestRecord = extractLatestAbwRecord(abwPayload);
        const feedRecord = extractObjectData(feedPayload);
        const growthRecords = extractArrayFromPayload(growthPayload);
        const latestGrowthValue = growthRecords[growthRecords.length - 1];
        const latestGrowthRecord = isRecord(latestGrowthValue) ? latestGrowthValue : {};
        return {
            abw: toNullableNumber(latestRecord.abw),
            survivalPercentage: toNullableNumber(latestRecord.survival_percentage) ??
                toNullableNumber(latestGrowthRecord.survival_percentage),
            biomass: toNullableNumber(latestRecord.biomass) ??
                toNullableNumber(latestGrowthRecord.biomass ?? latestGrowthRecord.total_biomass),
            feedDispensed: toNullableNumber(feedRecord.today_dispensed_feed ?? feedRecord.total_dispensed_feed),
            doc: toNullableNumber(latestRecord.doc) ?? fallback.doc,
            alertsCount: fallback.alertsCount,
        };
    }
    catch {
        return fallback;
    }
};
export const createPond = async (payload) => {
    const resolvedLocationId = resolveCreateLocationId(payload);
    return toPond(extractPondRecord(await request('POST', '/api/v3/ponds', toApiCreatePayload(payload, resolvedLocationId))));
};
export const updatePond = async (id, payload) => toPond(extractPondRecord(await request('PATCH', `/api/v3/ponds/${id}`, toApiMutationPayload(payload))));
export const deletePond = async (id, confirmationText) => void (await request('DELETE', `/api/v3/ponds/${id}`, { confirmationText }));
export const activatePond = async (id, payload) => {
    // BFF activatePondSchema requires ISO 8601 datetime; ensure date-only strings get time appended
    const startDate = payload.stockingDate.includes('T')
        ? payload.stockingDate
        : `${payload.stockingDate}T00:00:00.000Z`;
    // BFF enum only accepts Vannamei | Monodon | Fish — map 'Other' to undefined (optional field)
    const speciesType = payload.speciesType === 'Other' ? undefined : payload.speciesType;
    return toPond(extractPondRecord(await request('POST', `/api/v3/ponds/${id}/activate`, {
        start_date: startDate,
        post_larva_stocked: payload.plsStocked,
        ...(payload.initialAbw !== undefined ? { initial_abw: payload.initialAbw } : {}),
        ...(payload.hatcheryName ? { hatchery_name: payload.hatcheryName } : {}),
        ...(speciesType ? { species_type: speciesType } : {}),
        ...(payload.aeratorHp !== undefined ? { aerator_hp: payload.aeratorHp } : {}),
        ...(payload.pondSize !== undefined ? { size: payload.pondSize, units: 'ha' } : {}),
    })));
};
export const restorePond = async (id) => toPond(extractPondRecord(await request('POST', `/api/v3/ponds/${id}/restore`, {})));
export const toggleTestPond = async (id, isTest) => void (await request('PATCH', `/api/v3/ponds/${id}/test`, { is_test: isTest }));
export const toggleAlerts = async (id, pauseAlerts) => void (await request('PATCH', `/api/v3/ponds/${id}/alerts`, { pause_alerts: pauseAlerts }));
export const fetchPondCounts = async (locationId) => {
    const resolvedLocationId = locationId ?? readActiveLocationId();
    const url = buildUrl('/api/v3/ponds/counts', { location_id: resolvedLocationId });
    const locationHeaders = resolvedLocationId
        ? { 'x-location-id': resolvedLocationId }
        : {};
    const payload = await requestUrl('GET', url, locationHeaders);
    const data = isRecord(payload) && isRecord(payload.data) ? payload.data : {};
    return {
        active: toNumber(data.active),
        inactive: toNumber(data.inactive),
        deleted: toNumber(data.deleted),
    };
};
/** Extract GeoJSON coordinates from an API pond record. Returns undefined when absent or invalid. */
const toPondGeometry = (value) => {
    if (!isRecord(value))
        return undefined;
    const geo = value.geometry;
    if (!isRecord(geo))
        return undefined;
    // Allow missing type (legacy V2 ponds may omit it); reject anything explicitly non-polygon
    if (geo.type && String(geo.type).toLowerCase() !== 'polygon')
        return undefined;
    if (!Array.isArray(geo.coordinates) || geo.coordinates.length === 0)
        return undefined;
    // Handle both storage formats used by legacy and V3 ponds:
    //   3D GeoJSON:  coordinates = [[[lng, lat], ...]]  → coordinates[0] is the outer ring
    //   2D legacy:   coordinates = [[lng, lat], ...]    → coordinates itself is the outer ring
    const firstElement = geo.coordinates[0];
    if (!Array.isArray(firstElement))
        return undefined;
    const ring = typeof firstElement[0] === 'number'
        ? geo.coordinates // 2D flat array — treat as outer ring
        : firstElement; // 3D GeoJSON — first element is the outer ring
    if (ring.length < 3)
        return undefined;
    return {
        type: 'Polygon',
        coordinates: [ring],
    };
};
/**
 * Fetch all ponds including their GeoJSON polygon geometry, for map rendering.
 * Hits the same /api/v3/ponds endpoint but maps geometry from each record.
 */
export const fetchPondsWithGeometry = async (options = {}) => {
    const page = options.page ?? 1;
    const limit = Math.min(options.limit ?? 100, 200);
    const url = buildUrl('/api/v3/ponds', {
        location_id: options.locationId,
        page,
        limit,
        status: toApiStatus(options.status),
    });
    const payload = await requestUrl('GET', url);
    const rawPonds = extractPondArray(payload);
    return rawPonds
        .map((raw) => {
        const base = toPondListItem(raw);
        if (!base)
            return null;
        return {
            ...base,
            geometry: toPondGeometry(raw),
        };
    })
        .filter((p) => p !== null);
};
/**
 * Fetch latest DO & temperature values for all ponds in a location.
 * Used to colour-code pond polygons on the map.
 */
export const fetchLatestDoAndTemp = async (locationId) => {
    try {
        const url = buildUrl('/api/v3/ponds/latest-do-temp', { location_id: locationId });
        const payload = await requestUrl('GET', url);
        const records = extractArrayFromPayload(payload);
        return records
            .filter(isRecord)
            .map((r) => ({
            pondId: typeof r.pond_id === 'string' ? r.pond_id : String(r._id ?? r.id ?? ''),
            doValue: toNullableNumber(r.do_value ?? r.dissolved_oxygen),
            tempValue: toNullableNumber(r.temp_value ?? r.temperature),
            criticalLowerLimit: toNumber(r.critical_lower_limit ?? 3),
            lowerLimit: toNumber(r.lower_limit ?? 4),
        }))
            .filter((d) => d.pondId !== '');
    }
    catch {
        return [];
    }
};
